import React, { useCallback, useEffect, useRef, useState } from 'react';
import { importacionErpApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { FileText, FileSpreadsheet, Upload, RefreshCw, CheckCircle, AlertTriangle, X } from 'lucide-react';
import { toast } from 'sonner';

const fmtBytes = (b) => b < 1024 * 1024 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`;
const fmtNum  = (n) => new Intl.NumberFormat('es-ES').format(n || 0);

// ── Zona de drop (multi-archivo) ──────────────────────────────────────────────
const DropZone = ({ tipo, files, onFiles, onRemove, accept, icon: Icon, titulo, descripcion, formato }) => {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef();
  const hasFiles = files.length > 0;

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    onFiles(Array.from(e.dataTransfer.files));
  }, [onFiles]);

  const handleChange = (e) => {
    onFiles(Array.from(e.target.files));
    e.target.value = '';
  };

  return (
    <div className="space-y-2">
      <div
        onDrop={handleDrop}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onClick={() => inputRef.current?.click()}
        className={`relative flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed cursor-pointer select-none transition-colors
          ${hasFiles ? 'p-4' : 'p-8'}
          ${dragging ? 'border-brand-400 bg-brand-50' : hasFiles ? 'border-brand-200 bg-brand-50/30 hover:border-brand-300' : 'border-ink-200 hover:border-brand-300 hover:bg-brand-50/40'}`}
        data-testid={`dropzone-${tipo}`}
      >
        <input ref={inputRef} type="file" accept={accept} multiple className="hidden" onChange={handleChange} />
        {hasFiles ? (
          <div className="flex items-center gap-2 text-brand-600 text-sm">
            <Upload className="w-4 h-4" /> Añadir más archivos
          </div>
        ) : (
          <>
            <Icon className="w-10 h-10 text-ink-300" />
            <div className="text-center">
              <p className="font-semibold text-ink-800">{titulo}</p>
              <p className="text-xs text-ink-500 mt-1">{descripcion}</p>
              <p className="text-[11px] text-ink-400 mt-0.5 font-mono">{formato}</p>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-brand-600 mt-1">
              <Upload className="w-3.5 h-3.5" /> Arrastra aquí o haz clic
            </div>
          </>
        )}
      </div>
      {hasFiles && (
        <div className="space-y-1.5" data-testid={`filelist-${tipo}`}>
          {files.map((f, i) => (
            <div key={i} className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-emerald-200 bg-emerald-50" data-testid={`file-item-${tipo}-${i}`}>
              <div className="flex items-center gap-2 min-w-0">
                <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
                <span className="text-sm font-medium text-emerald-800 truncate">{f.name}</span>
                <span className="text-xs text-emerald-600 shrink-0">{fmtBytes(f.size)}</span>
              </div>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onRemove(i); }}
                className="p-1 rounded hover:bg-emerald-100 text-emerald-600 shrink-0"
                title="Quitar"
                data-testid={`file-remove-${tipo}-${i}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Resultado de un archivo individual ───────────────────────────────────────
const FilaResultado = ({ item }) => {
  const [errExpanded, setErrExpanded] = useState(false);
  const es_pdf = 'total_paginas' in item;

  if (item.ok === false) {
    return (
      <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
        <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-medium text-red-800 truncate">{item.archivo}</p>
          <p className="text-xs text-red-600">{item.error || 'Error desconocido'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 rounded-lg bg-white border border-ink-200 space-y-2">
      <div className="flex items-center gap-2">
        <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
        <p className="text-sm font-medium text-ink-800 truncate">{item.archivo}</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {es_pdf ? (
          <>
            <Stat label="Páginas"       value={fmtNum(item.total_paginas)} />
            <Stat label="Procesadas"    value={fmtNum(item.facturas_procesadas)} />
            <Stat label="Nuevas"        value={fmtNum(item.facturas_nuevas)} tone="good" />
            <Stat label="Actualizadas"  value={fmtNum(item.facturas_actualizadas)} />
          </>
        ) : (
          <>
            <Stat label="Filas CSV"     value={fmtNum(item.total_filas_csv)} />
            <Stat label="Docs nuevos"   value={fmtNum(item.documentos_nuevos)} tone="good" />
            <Stat label="Actualizados"  value={fmtNum(item.documentos_actualizados)} />
            <Stat label="Líneas"        value={fmtNum(item.lineas_totales)} />
          </>
        )}
      </div>
      {item.entidades_creadas > 0 && (
        <p className="text-xs text-violet-700 bg-violet-50 px-2 py-1 rounded">
          {fmtNum(item.entidades_creadas)} entidades nuevas creadas (pendientes de revisión)
        </p>
      )}
      {item.errores?.length > 0 && (
        <div>
          <button onClick={() => setErrExpanded((v) => !v)} className="text-xs text-red-600 underline">
            {item.errores.length} error{item.errores.length > 1 ? 'es' : ''} — ver detalle
          </button>
          {errExpanded && (
            <div className="mt-1 max-h-32 overflow-y-auto bg-red-50 rounded p-2 space-y-1">
              {item.errores.map((e, i) => (
                <p key={i} className="text-xs text-red-700 font-mono">
                  {e.pagina ? `Pág ${e.pagina}: ` : ''}{e.mensaje || JSON.stringify(e)}
                </p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, tone }) => (
  <div className={`rounded-md p-2 border ${tone === 'good' ? 'bg-emerald-50 border-emerald-200' : 'bg-ink-50 border-ink-200'}`}>
    <div className="text-[10px] text-ink-500 uppercase tracking-wide mb-0.5">{label}</div>
    <div className={`text-lg font-bold ${tone === 'good' ? 'text-emerald-700' : 'text-ink-900'}`}>{value}</div>
  </div>
);

// ── Página principal ──────────────────────────────────────────────────────────
export default function ImportacionERP() {
  const [pdfFiles, setPdfFiles]     = useState([]);
  const [csvFiles, setCsvFiles]     = useState([]);
  const [dryRun, setDryRun]         = useState(false);
  const [loading, setLoading]       = useState(false);
  const [progreso, setProgreso]     = useState('');
  const [resultados, setResultados] = useState([]);
  const [resumen, setResumen]       = useState(null);

  useEffect(() => {
    importacionErpApi.resumen().then(setResumen).catch(() => {});
  }, []);

  const handlePdfFiles = (nuevos) => {
    const validos   = nuevos.filter((f) => f.name.toLowerCase().endsWith('.pdf'));
    const invalidos = nuevos.filter((f) => !f.name.toLowerCase().endsWith('.pdf'));
    if (invalidos.length) toast.error('Solo se aceptan archivos PDF en esta zona');
    if (validos.length)   setPdfFiles((prev) => [...prev, ...validos]);
  };

  const handleCsvFiles = (nuevos) => {
    const validos   = nuevos.filter((f) => f.name.toLowerCase().endsWith('.csv'));
    const invalidos = nuevos.filter((f) => !f.name.toLowerCase().endsWith('.csv'));
    if (invalidos.length) toast.error('Solo se aceptan archivos CSV en esta zona');
    if (validos.length)   setCsvFiles((prev) => [...prev, ...validos]);
  };

  const waitForJob = (jobId) => new Promise((resolve, reject) => {
    const iv = setInterval(async () => {
      try {
        const job = await importacionErpApi.estadoJob(jobId);
        if (job.estado === 'completado') { clearInterval(iv); resolve(job.resultado); }
        else if (job.estado === 'error') { clearInterval(iv); reject(new Error(job.error || 'Error en el job')); }
      } catch (e) { clearInterval(iv); reject(e); }
    }, 3000);
  });

  const handleImportar = async () => {
    const total = pdfFiles.length + csvFiles.length;
    if (!total) { toast.error('Selecciona al menos un archivo'); return; }
    setLoading(true);
    setResultados([]);
    const acc = [];
    let n = 0;

    const procesar = async (file, apiFn) => {
      n++;
      setProgreso(`Procesando ${file.name} (${n}/${total})…`);
      try {
        let res = await apiFn(file, dryRun);
        if (res.job_id) res = await waitForJob(res.job_id);
        acc.push({ archivo: file.name, ok: true, ...res });
      } catch (e) {
        acc.push({ archivo: file.name, ok: false, error: e.message });
        toast.error(`${file.name}: ${e.message}`);
      }
      setResultados([...acc]);
    };

    try {
      for (const f of pdfFiles) await procesar(f, importacionErpApi.importarPdf);
      for (const f of csvFiles) await procesar(f, importacionErpApi.importarCsv);
      if (!dryRun) importacionErpApi.resumen().then(setResumen).catch(() => {});
      const ok = acc.filter((r) => r.ok !== false).length;
      toast.success(`${ok} de ${total} archivo${total > 1 ? 's' : ''} procesado${total > 1 ? 's' : ''} correctamente`);
    } finally {
      setLoading(false);
      setProgreso('');
    }
  };

  const totalArchivos = pdfFiles.length + csvFiles.length;

  return (
    <div className="space-y-6" data-testid="importacion-erp-page">
      <div>
        <h1 className="text-3xl font-bold text-ink-900 font-display tracking-tight">Importación ERP</h1>
        <p className="text-sm text-ink-500 mt-1">Importa facturas (PDF Crystal Reports) y pedidos (CSV Inology)</p>
      </div>

      {/* Estadísticas rápidas */}
      {resumen && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <Card className="p-3 border-ink-200">
            <div className="text-xs text-ink-500 uppercase tracking-wide mb-1">Total documentos</div>
            <div className="text-xl font-bold text-ink-900">{fmtNum(resumen.total_documentos)}</div>
          </Card>
          {Object.entries(resumen.por_tipo || {}).slice(0, 3).map(([tipo, origs]) => {
            const tot = Object.values(origs).reduce((s, o) => s + (o.total || 0), 0);
            return (
              <Card key={tipo} className="p-3 border-ink-200">
                <div className="text-xs text-ink-500 uppercase tracking-wide mb-1">{tipo}</div>
                <div className="text-xl font-bold text-ink-900">{fmtNum(tot)}</div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Zonas de drop */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <DropZone
          tipo="pdf"
          files={pdfFiles}
          onFiles={handlePdfFiles}
          onRemove={(i) => setPdfFiles((prev) => prev.filter((_, idx) => idx !== i))}
          accept=".pdf"
          icon={FileText}
          titulo="Facturas (PDF)"
          descripcion="Exportado desde Crystal Reports — una página = una factura"
          formato="FacturaNEW_AGUAZOR.rpt → PDF"
        />
        <DropZone
          tipo="csv"
          files={csvFiles}
          onFiles={handleCsvFiles}
          onRemove={(i) => setCsvFiles((prev) => prev.filter((_, idx) => idx !== i))}
          accept=".csv"
          icon={FileSpreadsheet}
          titulo="Pedidos (CSV)"
          descripcion="CSV exportado desde Crystal Reports — formato Inology estándar"
          formato="PEDIDOS.csv"
        />
      </div>

      {/* Controles */}
      <div className="flex items-center gap-4 flex-wrap">
        <label className="flex items-center gap-2 text-sm text-ink-700 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={dryRun}
            onChange={(e) => setDryRun(e.target.checked)}
            className="rounded"
            data-testid="chk-dry-run"
          />
          Simulación (dry-run) — no guarda nada
        </label>
        <Button
          onClick={handleImportar}
          disabled={loading || totalArchivos === 0}
          data-testid="btn-importar"
        >
          {loading ? (
            <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> {progreso || 'Importando…'}</>
          ) : (
            <><Upload className="w-4 h-4 mr-2" /> Importar{totalArchivos > 0 ? ` (${totalArchivos})` : ''}</>
          )}
        </Button>
        {dryRun && (
          <span className="text-xs bg-amber-100 text-amber-700 border border-amber-200 px-2 py-1 rounded">
            Modo simulación — los datos NO se guardarán
          </span>
        )}
      </div>

      {/* Barra de progreso */}
      {loading && progreso && (
        <div className="bg-brand-50 border border-brand-200 rounded-lg p-4 flex items-center gap-3">
          <RefreshCw className="w-5 h-5 text-brand-500 animate-spin shrink-0" />
          <p className="text-sm font-medium text-brand-800">{progreso}</p>
        </div>
      )}

      {/* Resultados */}
      {resultados.length > 0 && (
        <Card className="p-5 border-ink-200 bg-white space-y-3" data-testid="resultado-importacion">
          <h3 className="font-semibold text-ink-800 flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-emerald-500" />
            Resultado de la importación ({resultados.length} archivo{resultados.length > 1 ? 's' : ''})
          </h3>
          <div className="space-y-3">
            {resultados.map((item, i) => <FilaResultado key={i} item={item} />)}
          </div>
          {resultados.length > 1 && (
            <div className="pt-3 border-t border-ink-100">
              <p className="text-xs text-ink-500 font-medium mb-2 uppercase tracking-wide">Totales acumulados</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Stat label="Facturas nuevas"  value={fmtNum(resultados.reduce((s, r) => s + (r.facturas_nuevas || 0), 0))} tone="good" />
                <Stat label="Docs nuevos"      value={fmtNum(resultados.reduce((s, r) => s + (r.documentos_nuevos || 0), 0))} tone="good" />
                <Stat label="Actualizados"     value={fmtNum(resultados.reduce((s, r) => s + (r.facturas_actualizadas || 0) + (r.documentos_actualizados || 0), 0))} />
                <Stat label="Entidades creadas" value={fmtNum(resultados.reduce((s, r) => s + (r.entidades_creadas || 0), 0))} />
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
